/**
 * Logo Generation API Service
 * Handles API calls with authentication (Auth0 JWT or guest token)
 */

import { getGuestToken } from '../auth/guestToken';

// Use relative URL - Vite proxy handles routing to backend
const API_BASE_URL = '/api/v1';

// Store for auth token getter and state (set by App.jsx or GeneratorContext.jsx)
let getAuthToken = null;
let isAuthenticated = false;

/**
 * Configure the API client with auth functions
 * Called from App.jsx after Auth0 initializes
 */
export function configureAuth(authConfig) {
  getAuthToken = authConfig.getAccessTokenSilently;
  isAuthenticated = authConfig.isAuthenticated;
}

/**
 * Update authenticated state (kept for backwards compatibility)
 */
export function setAuthenticated(authenticated) {
  // No longer used - we rely on getAuthToken being set instead
}


/**
 * Custom error for limit exceeded
 */
export class LimitExceededError extends Error {
  constructor(message, isGuest) {
    super(message);
    this.name = 'LimitExceededError';
    this.isGuest = isGuest;
  }
}

/**
 * Custom error for signup required response
 */
export class SignupRequiredError extends Error {
  constructor(message) {
    super(message);
    this.name = 'SignupRequiredError';
  }
}

/**
 * Custom error for email not verified
 */
export class EmailNotVerifiedError extends Error {
  constructor(message) {
    super(message);
    this.name = 'EmailNotVerifiedError';
  }
}

/**
 * Make an authenticated API request
 */
async function apiRequest(endpoint, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Only try to get auth token if user is authenticated and getter is available
  // and they are not trying to explicitly use guest mode for a specific request
  if (isAuthenticated === true && getAuthToken && !options.skipAuth) {
    try {
      const token = await getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    } catch (error) {
      // If we get "Login required", it means the Auth0 state is out of sync
      // We log it but proceed to try the guest token if available
      console.warn('Auth token fetch skipped (session may have expired):', error.message || error);
    }
  }

  // If no auth token was set, try guest token
  if (!headers['Authorization']) {
    const guestToken = getGuestToken();
    if (guestToken) {
      headers['X-Guest-Token'] = guestToken;
    }
  }

  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // Handle 403 errors (signup required, email not verified, etc.)
  if (response.status === 403) {
    const error = await response.json().catch(() => null);
    if (error) {
      if (error.error?.code === 'SIGNUP_REQUIRED' || error.code === 'SIGNUP_REQUIRED') {
        throw new SignupRequiredError(error.error?.message || error.message || 'Signup required');
      }
      if (error.error?.code === 'EMAIL_NOT_VERIFIED') {
        throw new EmailNotVerifiedError(error.error?.message || 'Please verify your email address');
      }
    }
  }

  // Check content type before parsing as JSON
  const contentType = response.headers.get('content-type');
  if (!contentType || !contentType.includes('application/json')) {
    const text = await response.text();
    console.error('Expected JSON, got:', text.substring(0, 100));
    throw new Error('Server returned an unexpected response format. Please try again later.');
  }

  return response;
}

/**
 * Generate logo options based on a prompt
 * @param {{prompt: string, platform?: string, transparentBackground?: boolean, colorHints?: {color: string, purpose?: string}[], logoType?: string}} request
 * @returns {Promise<{logos: Array, platform?: string}>}
 */
export async function generateLogos(request) {
  const response = await apiRequest('/logos/generate', {
    method: 'POST',
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));

    if (error.error?.code === 'LIMIT_EXCEEDED' || error.error?.code === 'GUEST_LIMIT_EXCEEDED') {
      throw new LimitExceededError(
        error.error?.message || 'Limit exceeded',
        error.error?.code === 'GUEST_LIMIT_EXCEEDED'
      );
    }

    throw new Error(error.error?.message || 'Failed to generate icons');
  }

  return response.json();
}

/**
 * Generate refined logo variations based on an existing logo
 * @param {Object} baseLogo - The logo to refine from
 * @param {string} refinementPrompt - Description of desired changes
 * @param {Object} options - Additional options like transparentBackground
 * @returns {Promise<{logos: Array, platform?: string, refinementPrompt: string}>}
 */
export async function refineLogo(baseLogo, refinementPrompt, options = {}) {
  const response = await apiRequest('/logos/refine', {
    method: 'POST',
    body: JSON.stringify({
      logoId: baseLogo.id,
      refinementPrompt,
      transparentBackground: options.transparentBackground,
    }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));

    if (error.error?.code === 'LIMIT_EXCEEDED' || error.error?.code === 'GUEST_LIMIT_EXCEEDED') {
      throw new LimitExceededError(
        error.error?.message || 'Limit exceeded',
        error.error?.code === 'GUEST_LIMIT_EXCEEDED'
      );
    }

    throw new Error(error.error?.message || 'Failed to refine icon');
  }

  return response.json();
}

/**
 * Upload a base icon (1:1) to be used as a refinement starting point.
 * Accepts either raw base64 bytes or a data URL.
 * @param {string} imageData
 * @returns {Promise<{id: string, url: string, thumbnailUrl: string}>}
 */
export async function uploadLogo(imageData) {
  const response = await apiRequest('/logos/upload', {
    method: 'POST',
    body: JSON.stringify({ imageData }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to upload icon');
  }

  return response.json();
}

/**
 * Prepare logo for download in specified sizes
 * @param {string} logoId - ID of the logo to download
 * @param {string} platform - Target platform
 * @param {string[]} sizes - Array of size strings to include (e.g., ['1024x1024', '512x512'])
 * @returns {Promise<{downloadUrl: string, files: Array}>}
 */
export async function downloadLogo(logoId, platform, sizes = []) {
  const response = await apiRequest(`/logos/${logoId}/download`, {
    method: 'POST',
    body: JSON.stringify({ platform, sizes }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to prepare download');
  }

  return response.json();
}

/**
 * Prepare a single ZIP containing assets for multiple platforms.
 * @param {string} logoId - ID of the logo to download
 * @param {{platform: string, sizes: string[]}[]} targets - Platform + sizes to include
 * @returns {Promise<{downloadUrl: string, files: Array}>}
 */
export async function downloadLogoBundle(logoId, targets = []) {
  const response = await apiRequest(`/logos/${logoId}/download/bundle`, {
    method: 'POST',
    body: JSON.stringify({ targets }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));

    // Check for email not verified error
    if (response.status === 403 && error.error?.code === 'EMAIL_NOT_VERIFIED') {
      throw new EmailNotVerifiedError(
        error.error?.message || 'Please verify your email address to download icons'
      );
    }

    // Check for insufficient credits error
    if (response.status === 402) {
      const err = new InsufficientCreditsError(
        error.error?.message || 'Insufficient credits',
        error.error?.required,
        error.error?.available
      );
      throw err;
    }

    throw new Error(error.error?.message || 'Failed to prepare download');
  }

  return response.json();
}

/**
 * Custom error for insufficient credits
 */
export class InsufficientCreditsError extends Error {
  constructor(message, required, available) {
    super(message);
    this.name = 'InsufficientCreditsError';
    this.required = required;
    this.available = available;
  }
}



/**
 * Get current user's credit balance
 * @returns {Promise<{balance: number, downloadCost: number}>}
 */
export async function getCredits() {
  const response = await apiRequest('/users/me/credits');

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to fetch credits');
  }

  return response.json();
}

/**
 * Get user's credit transaction history
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<{transactions: Array, page: number, size: number, total: number}>}
 */
export async function getCreditHistory(page = 0, size = 20) {
  const response = await apiRequest(`/users/me/credits/history?page=${page}&size=${size}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to fetch credit history');
  }

  return response.json();
}

/**
 * Get user's generated logos (history)
 * @param {number} page - Page number (0-indexed)
 * @param {number} size - Page size
 * @returns {Promise<{logos: Array, page: number, size: number, total: number}>}
 */
export async function getUserLogos(page = 0, size = 12) {
  const response = await apiRequest(`/logos?page=${page}&size=${size}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to fetch icons');
  }

  return response.json();
}

/**
 * Get a specific logo by ID
 * @param {string} id - Logo ID
 * @returns {Promise<{id: string, url: string, thumbnailUrl: string, isPurchased: boolean}>}
 */
export async function getLogo(id) {
  const response = await apiRequest(`/logos/${id}`);

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error?.message || 'Failed to fetch icon');
  }

  return response.json();
}
